import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Box, Paper, Typography, IconButton } from '@mui/material';
import { Close } from '@mui/icons-material';
import { Popup } from '@/components';
import { isPhoneOrHighScreen } from '@/utilities/sizeUtils';
import { requestPlatformInformation } from '@/utilities/deviceInfoUtils';
import { fulldate } from '@/utilities/dateUtils';

import './SysinfoPopup.css';

function CloseComponent({ closePopup }: { closePopup: () => void }) {
  return (
    <IconButton
      className="close-button"
      aria-label="close"
      onClick={() => {
        closePopup();
      }}
      sx={{ color: 'var(--mui-palette-text-primary)' }}
      data-qa-element-id="popup-close-button"
    >
      <Close />
    </IconButton>
  );
}

export default function SysinfoPopup({ closePopup }: { closePopup: () => Promise<void> }) {
  const { t } = useTranslation();
  const history: { who: string; when: string; what: string }[] = ENVIRONMENT !== 'stable' ? JSON.parse(HISTORY) : [];
  const [platformVersion, setPlatformVersion] = useState<string | undefined>(undefined);

  const isMobile = isPhoneOrHighScreen() ? t('error.dialog.acknowledgeSymbol') : 'X';

  const title = t('mainMenu.systemInformation');

  async function getPlatformInformation() {
    if (!window.platformInformation) {
      return;
    }
    const platformInformation = await requestPlatformInformation();
    setPlatformVersion(platformInformation.app.version);
  }

  useEffect(() => {
    void getPlatformInformation();
  }, []);

  const body = (
    <div className="sysinfo-body">
      <div>
        <Typography className="sysinfo-block-title" data-qa-element-id="application_label">
          {t('systemInfo.application')}
        </Typography>
        <Paper className="sysinfo-box">
          <Box className="box">
            <div data-qa-element-id="systemInfo-buildtime" className="sysinfo">
              <div className="sysinfo-caption">{t('systemInfo.buildTime')}:</div>
              <div className="sysinfo-value">{BUILD_TIMESTAMP}</div>
            </div>
            <div data-qa-element-id="systemInfo-version" className="sysinfo">
              <div className="sysinfo-caption">{t('systemInfo.version')}:</div>
              <div className="sysinfo-value">
                {VERSION} {platformVersion && ' (' + platformVersion + ')'}
              </div>
            </div>
            <div data-qa-element-id="systemInfo-buildid" className="sysinfo">
              <div className="sysinfo-caption">{t('systemInfo.buildId')}:</div>
              <div className="sysinfo-value">{BUILD_NUMBER}</div>
            </div>
          </Box>
        </Paper>
      </div>

      <div>
        <Typography className="sysinfo-block-title" data-qa-element-id="system_label">
          {t('systemInfo.system')}
        </Typography>
        <Paper className="sysinfo-box">
          <Box className="box">
            <div data-qa-element-id="systemInfo-platform" className="sysinfo">
              <div className="sysinfo-caption">{t('systemInfo.platform')}:</div>
              <div className="sysinfo-value">{window.navigator.platform}</div>
            </div>
            <div data-qa-element-id="systemInfo-language" className="sysinfo">
              <div className="sysinfo-caption">{t('systemInfo.language')}:</div>
              <div className="sysinfo-value">{window.navigator.language}</div>
            </div>
            <div data-qa-element-id="systemInfo-useragent" className="sysinfo">
              <div className="sysinfo-caption">{t('systemInfo.browser')}:</div>
              <div className="sysinfo-value">{window.navigator.userAgent}</div>
            </div>
          </Box>
        </Paper>
      </div>

      <div>
        <Typography className="sysinfo-block-title" data-qa-element-id="device_label">
          {t('systemInfo.device')}
        </Typography>
        <Paper className="sysinfo-box">
          <Box className="box">
            <div data-qa-element-id="systemInfo-displaysize" className="sysinfo">
              <div className="sysinfo-caption">{t('systemInfo.displaySize')}:</div>
              <div className="sysinfo-value">
                {window.screen.width} x {window.screen.height}
              </div>
            </div>
            <div data-qa-element-id="systemInfo-resolution" className="sysinfo">
              <div className="sysinfo-caption">{t('systemInfo.resolution')}:</div>
              <div className="sysinfo-value">
                {Math.floor(window.screen.width * window.devicePixelRatio)} x{' '}
                {Math.floor(window.screen.height * window.devicePixelRatio)}
              </div>
            </div>
            <div data-qa-element-id="systemInfo-isphonescreen" className="sysinfo">
              <div className="sysinfo-caption">{t('systemInfo.phone')}:</div>
              <div className="sysinfo-value">{isMobile}</div>
            </div>
          </Box>
        </Paper>
      </div>

      {ENVIRONMENT !== 'stable' && (
        <>
          <div>
            <Typography className="sysinfo-block-title" data-qa-element-id="developer_label">
              {t('systemInfo.development')}
            </Typography>
            <Paper className="sysinfo-box">
              <Box className="box">
                <div data-qa-element-id="systemInfo-builduser" className="sysinfo">
                  <div className="sysinfo-caption">{t('systemInfo.buildUser')}:</div>
                  <div className="sysinfo-value">{USERNAME}</div>
                </div>
                <div data-qa-element-id="systemInfo-branchname" className="sysinfo">
                  <div className="sysinfo-caption">{t('systemInfo.branchName')}:</div>
                  <div className="sysinfo-value">{BRANCHNAME}</div>
                </div>
                <div data-qa-element-id="systemInfo-environment" className="sysinfo">
                  <div className="sysinfo-caption">{t('systemInfo.environment')}:</div>
                  <div className="sysinfo-value">{ENVIRONMENT}</div>
                </div>
                <div data-qa-element-id="systemInfo-last-commits" className="sysinfo">
                  <div className="sysinfo-caption">{t('systemInfo.lastCommits')}:</div>
                  <table data-qa-element-id="systemInfo-last-commits-table">
                    <tbody>
                      {history.map((h, i) => {
                        return (
                          <tr key={i}>
                            <td>{fulldate(new Date(h.when).getTime() * 1000, t)}</td>
                            <td>{h.who}</td>
                            <td style={{ wordBreak: 'break-word' }}>{h.what}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </Box>
            </Paper>
          </div>
        </>
      )}
    </div>
  );

  if (isPhoneOrHighScreen()) {
    return (
      <Popup
        popupTitle={title}
        header={undefined}
        body={body}
        footer={undefined}
        closePopup={closePopup}
        isFullScr={true}
        showCloseButton={true}
        leftText={true}
      />
    );
  }

  return (
    <>
      <div className="sysinfo-container" data-qa-element-id="sysinfo_view">
        <div className="sysinfo-header">
          <Typography variant="h6" className="sysinfo-container-title" data-qa-element-id="sysinfo_view_title">
            {title}
          </Typography>
          <CloseComponent closePopup={closePopup} />
        </div>
        {body}
      </div>
    </>
  );
}
